import React from 'react';
import { Footer } from '../components/Footer';
import Header from '../components/Header';

const PrivacyPolicy: React.FC = () => {
  
  return (
    <div className="bg-white min-h-screen">
      {/* Shared Header Component */}
      <Header />

      {/* Content with padding for fixed header */}
      <div className="pt-16">
        {/* Privacy Policy Content */}
        <div className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">
                Privacy Policy
              </h1>
              <p className="mt-4 text-gray-500">
                Last Updated: June 1, 2024
              </p>
            </div>

            <div className="bg-indigo-50 p-6 rounded-lg my-8">
              <h2 className="text-xl font-bold text-indigo-900 mb-3">Our Commitment to Security & Compliance</h2>
              <p className="text-gray-700 mb-3">
                Agilis AI is built on HIPAA-compliant cloud infrastructure with industry-standard security protections. We follow a modular compliance approach, prioritizing data security and privacy from day one while building toward additional certifications as we grow.
              </p>
              <p className="text-gray-700">
                As a clinical decision support platform, we are committed to maintaining the highest standards of data protection while providing tools that enhance healthcare workflows without replacing professional medical judgment.
              </p>
            </div>

            <div className="mt-12 prose prose-indigo prose-lg text-gray-500 mx-auto">
              <h2>Introduction</h2>
              <p>
                Agilis AI ("we," "our," or "us") is committed to protecting the privacy and security of your personal information. 
                This Privacy Policy describes how we collect, use, disclose, and safeguard your information when you use our 
                artificial intelligence-powered clinical decision support platform and related services (collectively, the "Services").
              </p>
              <p>
                We understand the sensitive nature of medical information and take our responsibility to protect it very seriously. 
                We are committed to complying with all applicable laws and regulations, including the Health Insurance Portability 
                and Accountability Act (HIPAA), the General Data Protection Regulation (GDPR), and other relevant privacy laws.
              </p>

              <h2>Information We Collect</h2>
              <p>We may collect several types of information from and about users of our Services, including:</p>
              <h3>1. Personal Information</h3>
              <ul>
                <li><strong>Healthcare Professionals:</strong> Name, email address, professional credentials, specialty, workplace information, and login credentials.</li>
                <li><strong>Patients:</strong> When healthcare professionals input patient data into our system, we may collect patient names, demographic information, medical history, symptoms, diagnoses, treatment plans, and other health-related information.</li>
              </ul>

              <h3>2. Usage Information</h3>
              <ul>
                <li>Information about how you use our Services, including queries submitted, features used, and time spent on the platform.</li>
                <li>Information about your device, including IP address, operating system, browser type, and device identifiers.</li>
                <li>Log data, including the pages you visit, the time and duration of your visits, and the search terms you use.</li>
              </ul>

              <h3>3. Audio and Visual Information</h3>
              <p>
                If you use our voice interface features or upload images and documents, we may collect audio recordings 
                of your voice and visual information from uploaded images and documents.
              </p>

              <h2>How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul>
                <li>Provide, maintain, and improve our Services, including AI-powered clinical workflow support and information retrieval.</li>
                <li>Develop new features, products, and services that enhance administrative efficiency for healthcare professionals.</li>
                <li>Process and complete transactions, and send related information including confirmations and invoices.</li>
                <li>Send technical notices, updates, security alerts, and support and administrative messages.</li>
                <li>Respond to your comments, questions, and requests and provide customer service.</li>
                <li>Communicate with you about products, services, offers, promotions, and events, and provide news and information we think will be of interest to you.</li>
                <li>Monitor and analyze trends, usage, and activities in connection with our Services.</li>
                <li>Detect, investigate, and prevent fraudulent transactions and other illegal activities and protect the rights and property of Agilis AI and others.</li>
                <li>Train and improve our artificial intelligence models to enhance the accuracy and effectiveness of our administrative and informational tools.</li>
                <li>Comply with legal obligations and regulatory requirements.</li>
              </ul>

              <h2>Legal Basis for Processing Personal Information (for EEA users)</h2>
              <p>
                If you are located in the European Economic Area (EEA), we collect and process your personal information 
                based on the following legal grounds:
              </p>
              <ul>
                <li><strong>Consent:</strong> When you have given your consent to the processing of your personal information.</li>
                <li><strong>Contractual Necessity:</strong> When processing is necessary for the performance of a contract with you or to take steps at your request before entering into a contract.</li>
                <li><strong>Legal Obligation:</strong> When processing is necessary for compliance with a legal obligation to which we are subject.</li>
                <li><strong>Legitimate Interests:</strong> When processing is necessary for our legitimate interests or those of a third party, except where such interests are overridden by your interests or fundamental rights and freedoms.</li>
              </ul>

              <h2>How We Share Your Information</h2>
              <p>We may share your personal information with:</p>
              <ul>
                <li><strong>Service Providers:</strong> We may share your information with third-party vendors, consultants, and other service providers who perform services on our behalf, such as cloud storage providers, analytics providers, and payment processors. All service providers are carefully selected to ensure they maintain adequate security and privacy protections.</li>
                <li><strong>Healthcare Organizations:</strong> If you are using our Services through your healthcare organization, we may share your information with that organization in accordance with our agreements with them.</li>
                <li><strong>Business Transfers:</strong> If Agilis AI is involved in a merger, acquisition, or sale of all or a portion of its assets, your information may be transferred as part of that transaction.</li>
                <li><strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).</li>
                <li><strong>Protection of Rights:</strong> We may disclose your information to protect the rights, property, or safety of Agilis AI, our users, or others.</li>
              </ul>
              <p>
                We do not sell or rent your personal information to third parties for their marketing purposes without your explicit consent.
              </p>

              <h2>Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect the personal information we collect and process. 
                These measures include:
              </p>
              <ul>
                <li><strong>Pre-certified Infrastructure:</strong> We build on HIPAA-compliant cloud services from trusted providers like AWS and Google Cloud.</li>
                <li><strong>Encryption:</strong> We use AES-256 encryption for data at rest and TLS 1.3 for data in transit.</li>
                <li><strong>Access Controls:</strong> We implement strict role-based access controls and authentication mechanisms.</li>
                <li><strong>Regular Security Reviews:</strong> We conduct ongoing security assessments and third-party penetration testing.</li>
                <li><strong>Employee Training:</strong> All staff receive regular security and privacy training.</li>
              </ul>
              <p>
                While we implement strong safeguards, no electronic transmission or storage method is 100% secure, and we cannot guarantee absolute security.
              </p>

              <h2>Data Retention</h2>
              <p>
                We retain personal information we collect from you for as long as necessary to fulfill the purposes for which we collected it, 
                including for the purposes of satisfying any legal, regulatory, tax, accounting, or reporting requirements. We may retain your 
                personal information for a longer period in the event of a complaint or if we reasonably believe there is a prospect of litigation.
              </p>

              <h2>Your Rights and Choices</h2>
              <p>Depending on your location, you may have certain rights regarding your personal information:</p>
              <ul>
                <li>Access to your personal information.</li>
                <li>Correction of inaccurate or incomplete information.</li>
                <li>Deletion of your personal information.</li>
                <li>Restriction or objection to processing of your personal information.</li>
                <li>Portability of your personal information.</li>
                <li>Withdrawal of consent, where we rely on consent as the legal basis for processing.</li>
              </ul>
              <p>
                To exercise these rights, please contact us using the information provided at the end of this Privacy Policy. 
                We may need to verify your identity before fulfilling your request.
              </p>

              <h2>Children's Privacy</h2>
              <p>
                Our Services are not intended for use by children under the age of 18, and we do not knowingly collect personal information 
                from children under 18. If you are a parent or guardian and believe we may have collected information about a child, please 
                contact us immediately.
              </p>

              <h2>International Data Transfers</h2>
              <p>
                We are based in Canada and Hong Kong, and your information may be processed in countries where we operate or have service providers. 
                These countries may have data protection laws that are different from those of your country. We implement appropriate safeguards to 
                protect your personal information when it is transferred internationally, such as Standard Contractual Clauses approved by the 
                European Commission.
              </p>

              <h2>Third-Party Links and Services</h2>
              <p>
                Our Services may contain links to third-party websites, applications, or services. We are not responsible for the privacy practices 
                or content of these third parties. We encourage you to read the privacy policies of these third parties before providing any 
                information to them.
              </p>

              <h2>Compliance Roadmap</h2>
              <p>
                We follow a modular compliance approach:
              </p>
              <ul>
                <li><strong>Current:</strong> HIPAA compliance and robust data security protections using pre-certified infrastructure.</li>
                <li><strong>Next Phase:</strong> ISO 27001 certification and expanded security frameworks.</li>
                <li><strong>Future:</strong> Additional certifications based on market and regulatory requirements.</li>
              </ul>
              <p>
                This approach allows us to maintain strong security and compliance while efficiently scaling our business.
              </p>

              <h2>Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. The updated version will be indicated by an updated "Last Updated" date, 
                and the updated version will be effective as soon as it is accessible. We encourage you to review this Privacy Policy frequently 
                to stay informed of how we are protecting your information.
              </p>

              <h2>Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy or our data practices, please contact us at:
              </p>
              <p>
                <strong>Privacy Officer</strong><br />
                AgilisCare<br />
              </p>
              <p>
                <strong>Email:</strong> info@agiliscare.com<br />
              </p>
              <p>
                For European users, please note that our EU representative can be contacted at: eu-representative@agiliscare.com
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PrivacyPolicy; 